/**
 * A metric is a measure that we use to assess the performance of the app.
 *
 * ## When you should use metrics
 *
 * You should use metrics if the thing you want to measure is:
 *    a) performance related
 *    b) an indicator of user experience problems
 *    c) tracking responsiveness to actions taken by the user, rather
 *    than the actions themselves.
 *
 * ## Differences between metrics.ts and analytics.ts
 *
 * 1. Conceptually, a metric measures performance on the front end, like rendering lag
 *    or event response timing. Analytics, on the other hand, should be used for tracking
 *    user actions taken and user behavior, like monthly active users of a feature.
 *
 * 2. Metrics are sent to our server,
 *    whereas analytics data are sent to Looker via Segment.
 *
 * ## How to use metrics
 *
 * There are two types of metrics: count and gauge.
 *
 * Count metrics send an occurrence for every call to metrics.count(),
 * and they are aggregated in dashboards.
 * For example: Fire a count every time we detect a button click that takes longer than x ms.
 *
 * Gauge metrics send a value to update to for every call to metrics.gauge(), and each
 * value is displayed as a data point in a time series. For example: Monitor the median
 * time it takes between typing and rendering text, and periodically record it via metrics.gauge().
 *
 * Metrics are collected and queued before being sent to the backend in batches for processing.
 * All queued metrics are sent to the backend every minute, and then the queue is cleared.
 *
 */

import BenchlingConfig from 'BenchlingConfig';
import lazyInit from 'util/lazyInit';
import * as net from 'util/Net';

// Keep in sync with METRIC_SHAPE in benchling/metrics/api.py.
export enum MetricType {
  COUNT = 'count',
  GAUGE = 'gauge',
  HISTOGRAM = 'histogram',
  DISTRIBUTION = 'distribution',
}

export interface Metric {
  name: string;
  value: number;
  tags: {[key: string]: string | number | boolean};
  type: MetricType;
}

type Preprocessor = (m: Array<Metric>) => Array<Metric>;

export interface MetricsQueue {
  push: (m: Metric) => void;
  inspect: () => Array<Metric>;
  addPreprocessor: (p: Preprocessor) => void;
  _flush: () => void;
}

export const getMetricsQueue: () => MetricsQueue = lazyInit(() => makeMetricsQueue(metricSendIntervalMs));

// Default to sending accumulated metrics every minute.
const metricSendIntervalMs: number = 60 * 1000;

/**
 * Sends a count type metric to the server.
 * @param metricName The name of the metric, by convention period separated.
 * @param value The number of counts to register, should usually be 1.
 * @param tags Tags to associate with this metric. WARNING: Tags are expensive,
 * both computationally and in $ cost. Avoid tags for which there would be many
 * unique combinations of key/value pairs.
 */
export function count(
  metricName: string,
  value: number = 1,
  tags: {[key: string]: string | number | boolean} | {} = {}
): void {
  const metric: Metric = {
    name: metricName,
    value,
    tags,
    type: MetricType.COUNT,
  };

  sendMetric(metric);
}

/**
 * Sends a gauge type metric to the server.
 * @param metricName The name of the metric, by convention period separated.
 * @param value The value to record for this time period for the metric,
 * @param tags Tags to associate with this metric. WARNING: Tags are expensive,
 * both computationally and in $ cost. Avoid tags for which there would be many
 * unique combinations of key/value pairs.
 */
export function gauge(
  metricName: string,
  value: number,
  tags: {[key: string]: string | number | boolean} | {} = {}
): void {
  const metric: Metric = {
    name: metricName,
    value,
    tags,
    type: MetricType.GAUGE,
  };

  sendMetric(metric);
}

/**
 * Sends a histogram type metric to the server.
 * @param metricName The name of the metric, by convention period separated.
 * @param value The value to record for this time period for the metric,
 * @param tags Tags to associate with this metric. WARNING: Tags are expensive,
 * both computationally and in $ cost. Avoid tags for which there would be many
 * unique combinations of key/value pairs.
 */
export function histogram(
  metricName: string,
  value: number,
  tags: {[key: string]: string | number | boolean} | {} = {}
): void {
  const metric: Metric = {
    name: metricName,
    value,
    tags,
    type: MetricType.HISTOGRAM,
  };

  sendMetric(metric);
}

/**
 * Sends a distribution type metric to the server.
 * @param metricName The name of the metric, by convention period separated.
 * @param value The value to record for this time period for the metric,
 * @param tags Tags to associate with this metric. WARNING: Tags are expensive,
 * both computationally and in $ cost. Avoid tags for which there would be many
 * unique combinations of key/value pairs.
 */
export function distribution(
  metricName: string,
  value: number,
  tags: {[key: string]: string | number | boolean} | {} = {}
): void {
  const metric: Metric = {
    name: metricName,
    value,
    tags,
    type: MetricType.DISTRIBUTION,
  };

  sendMetric(metric);
}

function sendMetric(metric: Metric): void {
  if (!BenchlingConfig.ENABLE_FRONTEND_METRICS) {
    return;
  }

  const metricsQ = getMetricsQueue();
  metricsQ.push(metric);
}

function makeMetricsQueue(intervalMs: number): MetricsQueue {
  let _mq: Array<Metric> = [];
  const preprocessors: Array<Preprocessor> = [];

  function send(): void {
    if (_mq.length > 0) {
      const postProcess = preprocess(_mq);
      _mq = [];
      const metricsPromise = net.postJSONPromise({
        url: '/1/api/metrics',
        data: {
          metricData: postProcess.map((metric) => ({
            metricType: metric.type,
            metricName: metric.name,
            tags: metric.tags,
            value: metric.value,
          })),
        },
      });
      metricsPromise
        .then((res) => {
          // Explicitly do nothing with the result, since metrics should have no impact on the user.
        })
        .catch((err) => {
          // Explicitly do nothing on error, since we do not want the user to know about failed metrics.
        });
    }
  }

  function preprocess(mq: Array<Metric>): Array<Metric> {
    return preprocessors.length > 0 ? preprocessors.reduce((prev, curr) => curr(prev), mq) : mq;
  }

  window.setInterval(send, intervalMs);
  // Send metrics on page unload only if we are running in Cypress. Otherwise it can cause a popup
  // oxlint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  if (window.Cypress) {
    window.addEventListener('beforeunload', (event) => {
      send();
    });
  }

  return {
    push(metric: Metric): void {
      _mq.push(metric);
    },
    inspect(): Array<Metric> {
      return [..._mq];
    },
    // Intended for use in testing.
    _flush(): void {
      send();
    },
    addPreprocessor(p: Preprocessor): void {
      preprocessors.push(p);
    },
  };
}

// Rollup metrics of the form `a.b.c.d.count` into each parent metric `a.count`, `a.b.count`, `a.b.c.count`,
// retaining the originals.
export function createMetricRollupSumProcessor(
  prefix: string,
  type: MetricType,
  metricSuffix: string
): Preprocessor {
  function process(mq: Array<Metric>): Array<Metric> {
    const applicableMetrics = mq.filter(
      (metric) => metric.type === type && metric.name.startsWith(prefix) && metric.name.endsWith(metricSuffix)
    );
    const suffixRegex = RegExp(`\\.${metricSuffix}$`);
    let processingMetrics = applicableMetrics.map((metric) => ({
      name: metric.name.replace(suffixRegex, ''),
      value: metric.value,
    }));

    const rollups = new Map<string, number>();
    // remove trailing section and sum until all names are empty
    while (processingMetrics.length > 0) {
      for (const metric of processingMetrics) {
        metric.name = metric.name.split('.').slice(0, -1).join('.');
      }
      processingMetrics = processingMetrics.filter((metric) => metric.name.length > 0);
      for (const metric of processingMetrics) {
        const rollup = rollups.get(metric.name);
        rollups.set(metric.name, (rollup ?? 0) + metric.value);
      }
    }

    // add suffix back to rollup metrics and add to queue
    for (const [key, value] of rollups) {
      mq.push({
        name: `${key}.${metricSuffix}`,
        value,
        tags: {},
        type,
      });
    }
    return mq;
  }
  return process;
}
